import React, { useEffect, useState, useRef } from 'react';
import * as XLSX from 'xlsx';
import 'handsontable/dist/handsontable.full.css';
import { HotTable } from '@handsontable/react';
import "../index.css";

interface ExcelEditorProps {
  file: File;
  onConfirm: (updatedFile: File) => void;
}

type SheetData = {
  name: string;
  data: string[][];
};

const ExcelEditor: React.FC<ExcelEditorProps> = ({ file, onConfirm }) => {
  const [sheets, setSheets] = useState<SheetData[]>([]);
  const [activeSheetIndex, setActiveSheetIndex] = useState(0);
  const [isDark, setIsDark] = useState(false);
  // const fileInputRef = useRef<HTMLInputElement | null>(null);

  const loadExcelFile = async (excelFile: File) => {
    const arrayBuffer = await excelFile.arrayBuffer();
    const workbook = XLSX.read(arrayBuffer, { type: 'array' });

    const allSheets = workbook.SheetNames.map(name => {
      const sheet = workbook.Sheets[name];
      const sheetData: any[][] = XLSX.utils.sheet_to_json(sheet, { header: 1 });
      return { name, data: sheetData.length ? sheetData : [[]] };
    });

    setSheets(allSheets);
    setActiveSheetIndex(0);
  };

  useEffect(() => {
    loadExcelFile(file);
  }, [file]);

  const activeSheet = sheets[activeSheetIndex];

  const updateCell = (row: number, col: number, value: any) => {
    setSheets(prev =>
      prev.map((sheet, i) =>
        i === activeSheetIndex
          ? {
              ...sheet,
              data: sheet.data.map((r, ri) =>
                ri === row
                  ? r.map((c, ci) => (ci === col ? value : c))
                  : r
              )
            }
          : sheet
      )
    );
  };

  // Add row
  const addRow = () => {
    setSheets(prev =>
      prev.map((sheet, i) =>
        i === activeSheetIndex
          ? { ...sheet, data: [...sheet.data, Array(sheet.data[0].length).fill('')] }
          : sheet
      )
    );
  };

  // Add column
  const addColumn = () => {
    setSheets(prev =>
      prev.map((sheet, i) =>
        i === activeSheetIndex
          ? {
              ...sheet,
              data: sheet.data.map(row => [...row, ''])
            }
          : sheet
      )
    );
  };

  const handleDownload = () => {
    const workbook = XLSX.utils.book_new();
    sheets.forEach(sheet => {
      const ws = XLSX.utils.aoa_to_sheet(sheet.data);
      XLSX.utils.book_append_sheet(workbook, ws, sheet.name);
    });

    const arrayBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([arrayBuffer], { type: "application/octet-stream" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "edited_menu.xlsx";
    link.click();
    URL.revokeObjectURL(url);
  };

  
  const handleUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const uploadedFile = event.target.files?.[0];
    if (uploadedFile) {
      loadExcelFile(uploadedFile);
    }
  };

  const handleConfirm = () => {
    const workbook = XLSX.utils.book_new();
    sheets.forEach(sheet => {
      const ws = XLSX.utils.aoa_to_sheet(sheet.data);
      XLSX.utils.book_append_sheet(workbook, ws, sheet.name);
    });
    const arrayBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const updatedFile = new File([arrayBuffer], 'confirmed_menu.xlsx', {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
    onConfirm(updatedFile);
  };

  if (!activeSheet) return <p>Loading sheets...</p>;

  return (
    <div className="mt-4 p-4 rounded bg-[#1e293b] shadow-md text-white w-full overflow-x-auto">
      <h2 className="text-xl font-semibold mb-4 text-[#cbd5e1] text-center">
        Edit Excel Menu
      </h2>

      <div className="flex gap-2 mb-4 flex-wrap justify-center">
        {sheets.map((sheet, index) => (
          <button
            key={sheet.name}
            onClick={() => setActiveSheetIndex(index)}
            className={`px-3 py-1 rounded font-semibold ${
              index === activeSheetIndex
                ? 'bg-sky-600 text-white'
                : 'bg-[#334155] text-[#cbd5e1]'
            }`}
          >
            {sheet.name}
          </button>
        ))}
      </div>

      <div className="flex gap-2 mb-2 justify-center">
        <button
          onClick={addRow}
          className="bg-green-600 hover:bg-green-800 px-3 py-1 rounded text-white"
        >
          + Add Row
        </button>
        <button
          onClick={addColumn}
          className="bg-blue-600 hover:bg-blue-800 px-3 py-1 rounded text-white"
        >
          + Add Column
        </button>
      </div>

      {HotTable ? (
        <div className="p-4">
          <div className={isDark ? "hot-theme-dark" : "hot-theme-light"}>
            <HotTable
              data={activeSheet.data}
              rowHeaders
              colHeaders={(col: number) => activeSheet.data[0][col] || `Col ${col + 1}`}
              width="100%"
              height={400}
              licenseKey="non-commercial-and-evaluation"
              manualRowMove
              manualColumnMove
              manualRowResize
              manualColumnResize
              contextMenu={['row_above', 'row_below', 'remove_row', 'remove_col', 'undo', 'redo']}
              stretchH="none"
              colWidths={(col: number) => {
                const text = activeSheet.data[0][col] || '';
                const min = 60;
                const max = 400;
                const approxCharWidth = 10;
                return Math.min(Math.max(text.length * approxCharWidth + 20, min), max);
              }}
              afterChange={(changes: any) => {
                if (!changes) return;
                changes.forEach(([row, col, oldVal, newVal]: any) => updateCell(row, col, newVal));
              }}
            />
          </div>
          <div className="mt-2 flex items-center gap-2">
            <span className="text-sm text-gray-700 dark:text-gray-300">
              {isDark ? "Dark Mode" : "Light Mode"}
            </span>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={isDark}
                onChange={() => setIsDark(!isDark)}
              />
              <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none rounded-full peer dark:bg-gray-600 peer-checked:bg-blue-600"></div>
              <div className="absolute left-0.5 top-0.5 w-5 h-5 bg-white rounded-full transition-transform peer-checked:translate-x-5"></div>
            </label>
          </div>
        </div>
      ) : null}

      <div className="flex gap-4 mt-4 justify-center">
        <button
          onClick={handleConfirm}
          className="bg-[#16a34a] hover:bg-[#14532d] text-white px-4 py-2 rounded"
        >
          Confirm and Convert to JSON
        </button>

        <button
          onClick={handleDownload}
          className="bg-sky-600 hover:bg-sky-800 text-white px-4 py-2 rounded"
        >
          Download Excel
        </button>
        
        <label className="bg-sky-600 hover:bg-sky-800 text-white px-4 py-2 rounded cursor-pointer">
          Upload Excel
          <input
            type="file"
            accept=".xlsx,.xls"
            onChange={handleUpload}
            className="hidden"
          />
        </label>
      </div>
    </div>
  );
};

export default ExcelEditor;
